import { PasswordProps } from 'antd/lib/input/Password';
import Text from 'components/Text/Text';
import { NamePath } from 'rc-field-form/lib/interface';
import React from 'react';
import { Form, Input, InputNumber } from 'antd';
import { Rule } from 'antd/lib/form';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import style from './FormInput.module.scss';

type TDefaultInputProps = Omit<PasswordProps, 'onChange'>;

interface IFormInput extends TDefaultInputProps {
	name: string;
	label: string;
	onChange?: (value: string) => void;
	onChangeNumber?: (value: number | null) => void;
	getValueFromEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder?: string;
	className?: string;
	containerClassName?: string;
	labelClassName?: string;
	validationRules?: Rule[];
	testId?: string;
	type?: 'text' | 'password' | 'textArea' | 'number';
	dependencies?: NamePath[];
	min?: number;
	max?: number;
	precision?: number;
}

const FormInput: React.FC<IFormInput> = ({
	name,
	label,
	placeholder = 'inputPlaceholder',
	onChange,
	onChangeNumber,
	getValueFromEvent,
	validationRules,
	testId,
	className,
	containerClassName,
	labelClassName,
	type = 'text',
	dependencies,
	min,
	max,
	precision,
	disabled = false,
	...restProps
}) => {
	const { t } = useTranslation('form');

	const containerClasses = classNames(style.container, containerClassName);
	const inputClasses = classNames(style.input, className);
	const textAreaClasses = classNames(style.textArea, className);
	const inputNumberClasses = classNames(style.inputNumber, className, { [style.disabled]: disabled });
	const labelClasses = classNames(style.label, labelClassName);

	const isRequired = validationRules?.some((rule) => {
		if (typeof rule === 'object') {
			return rule?.required;
		}
		return false;
	});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange?.(e.target.value);
	};

	const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onChange?.(e.target.value);
	};

	const handleChangeInputNumber = (e: Nullable<number>) => {
		onChangeNumber?.(e);
	};

	const renderInputElement = () => {
		switch (type) {
			case 'text':
				return (
					<Input
						className={inputClasses}
						onChange={!getValueFromEvent ? handleChange : undefined}
						allowClear={true}
						placeholder={t(placeholder)}
						disabled={disabled}
						data-test={`${testId}Input`}
						{...restProps}
					/>
				);
			case 'password':
				return (
					<Input.Password
						className={inputClasses}
						onChange={!getValueFromEvent ? handleChange : undefined}
						allowClear={true}
						placeholder={t(placeholder)}
						disabled={disabled}
						{...restProps}
					/>
				);
			case 'textArea':
				return (
					<Input.TextArea
						className={textAreaClasses}
						onChange={!getValueFromEvent ? handleChangeTextArea : undefined}
						allowClear={true}
						autoSize={true}
						placeholder={t(placeholder)}
					/>
				);
			case 'number':
				return (
					<InputNumber
						className={inputNumberClasses}
						onChange={!getValueFromEvent ? handleChangeInputNumber : undefined}
						placeholder={t(placeholder)}
						min={min}
						max={max}
						disabled={disabled}
						precision={precision}
					/>
				);
			default:
				return (
					<Input
						className={inputClasses}
						onChange={!getValueFromEvent ? handleChange : undefined}
						allowClear={true}
						placeholder={t(placeholder)}
						disabled={disabled}
						data-test={`${testId}Input`}
						{...restProps}
					/>
				);
		}
	};

	return (
		<div data-test={testId} className={containerClasses}>
			<div>
				<Text
					className={labelClasses}
					translationNameSpace="form"
					translationKey={label}
					size="xs"
					theme="neutralLight"
					display="inline"
				/>
				{!!isRequired && <Text translationKey="*" size="xs" theme="neutralLight" display="inline" />}
			</div>
			<Form.Item
				name={name}
				rules={validationRules}
				getValueFromEvent={getValueFromEvent}
				dependencies={dependencies}>
				{renderInputElement()}
			</Form.Item>
		</div>
	);
};

export default React.memo(FormInput);
